import { useState, useEffect } from "react";
import API, { formatError } from "@/api";
import { toDateTimeLocal } from "@/lib/utils";
import { toast } from "sonner";
import UserAvatar from "@/components/UserAvatar";
import { X, Loader2, Search, Copy } from "lucide-react";

const PRIORITY_OPTIONS = [
    { value: "low", label: "Scăzut" },
    { value: "medium", label: "Mediu" },
    { value: "high", label: "Ridicat" },
    { value: "critical", label: "Critic" },
];

// Pre-completează un modal de confirmare cu detaliile taskului sursă, apoi creează duplicatul.
export default function DuplicateTaskModal({ task, users = [], onClose, onDuplicated }) {
    const initialAssignees = task?.assignee_ids?.length
        ? task.assignee_ids
        : (task?.assignee_id ? [task.assignee_id] : []);

    const [title, setTitle] = useState(`${task?.title || "Task"} - duplicat`);
    const [description, setDescription] = useState(task?.description || "");
    const [assigneeIds, setAssigneeIds] = useState(initialAssignees);
    const [priority, setPriority] = useState(task?.priority || "medium");
    const [dueDate, setDueDate] = useState(toDateTimeLocal(task?.due_date));
    const [columnId, setColumnId] = useState(task?.column_id || "");
    const [columns, setColumns] = useState([]);
    const hasChecklist = (task?.checklist?.length || 0) > 0;
    const [copyChecklist, setCopyChecklist] = useState(true);
    const [loading, setLoading] = useState(false);
    const [assigneeSearch, setAssigneeSearch] = useState("");

    useEffect(() => {
        if (!task?.board_id) return;
        API.get(`/boards/${task.board_id}`)
            .then(r => {
                const cols = r.data?.columns || [];
                setColumns(cols);
                // dacă coloana sursă nu se mai regăsește, cade pe prima
                if (cols.length && !cols.some(c => c.id === (task?.column_id || ""))) {
                    setColumnId(cols[0].id);
                }
            })
            .catch(() => {});
    }, [task?.board_id, task?.column_id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim()) { toast.error("Titlul este obligatoriu"); return; }
        if (!columnId) { toast.error("Selectează o coloană"); return; }
        setLoading(true);
        try {
            const r = await API.post(`/tasks/${task.id}/duplicate`, {
                title: title.trim(),
                description,
                column_id: columnId,
                assignee_ids: assigneeIds,
                priority,
                due_date: dueDate ? new Date(dueDate).toISOString() : null,
                copy_checklist: hasChecklist ? copyChecklist : false,
            });
            toast.success("Duplicat creat cu succes");
            if (onDuplicated) onDuplicated(r.data);
            onClose();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative glass-panel rounded-2xl shadow-xl w-full max-w-md mx-4 z-10 max-h-[90vh] overflow-y-auto" data-testid="duplicate-task-modal">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[#e3e7f5] sticky top-0 bg-white/70 backdrop-blur-md rounded-t-2xl">
                    <h2 className="font-bold text-[#111827] text-base flex items-center gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        <Copy size={16} /> Duplică task
                    </h2>
                    <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#111827] transition-colors">
                        <X size={18} />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
                    <div>
                        <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Titlu *</label>
                        <input
                            data-testid="dup-task-title"
                            autoFocus
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            placeholder="Ce trebuie făcut?"
                            className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Coloană</label>
                        <select
                            data-testid="dup-task-column"
                            value={columnId}
                            onChange={e => setColumnId(e.target.value)}
                            className="w-full px-2 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white"
                        >
                            {columns.length === 0 && <option value={columnId}>Coloana curentă</option>}
                            {columns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Descriere</label>
                        <textarea
                            data-testid="dup-task-description"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder="Detalii opționale..."
                            rows={3}
                            className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Responsabili</label>
                        <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border border-[#E4E4E7] rounded-md min-h-[36px] bg-white">
                            {assigneeIds.length === 0 && <span className="text-xs text-[#9CA3AF]">Click pe nume pentru a atribui...</span>}
                            {assigneeIds.map(id => {
                                const u = users.find(x => x.id === id);
                                if (!u) return null;
                                return (
                                    <span key={id} className="inline-flex items-center gap-1 bg-blue-50 text-[#002FA7] rounded-full pl-1 pr-1.5 py-0.5 text-xs">
                                        <UserAvatar user={u} className="w-4 h-4" textClassName="text-[8px]" />
                                        {u.name}
                                        <button type="button" onClick={() => setAssigneeIds(assigneeIds.filter(x => x !== id))} className="ml-0.5 hover:text-red-500">×</button>
                                    </span>
                                );
                            })}
                        </div>
                        <div className="max-h-32 overflow-y-auto mt-1 border border-[#F1F3F5] rounded">
                            <div className="sticky top-0 bg-white border-b border-[#F1F3F5] px-1.5 py-1">
                                <div className="relative">
                                    <Search size={11} className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                                    <input
                                        type="text"
                                        value={assigneeSearch}
                                        onChange={e => setAssigneeSearch(e.target.value)}
                                        placeholder="Caută utilizator..."
                                        className="w-full pl-6 pr-2 py-1 text-xs border border-[#E4E4E7] rounded focus:outline-none focus:ring-1 focus:ring-[#002FA7] bg-white"
                                    />
                                </div>
                            </div>
                            <div className="p-1">
                                {(() => {
                                    const q = assigneeSearch.trim().toLowerCase();
                                    const filtered = users
                                        .filter(u => !assigneeIds.includes(u.id))
                                        .filter(u => {
                                            if (!q) return true;
                                            return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
                                        });
                                    if (filtered.length === 0) {
                                        return <div className="px-2 py-1.5 text-[11px] text-[#9CA3AF] text-center">{q ? "Niciun rezultat" : "Toți utilizatorii sunt deja atribuiți"}</div>;
                                    }
                                    return filtered.map(u => (
                                        <button key={u.id} type="button"
                                            onClick={() => setAssigneeIds([...assigneeIds, u.id])}
                                            className="w-full flex items-center gap-2 text-xs hover:bg-[#F8F9FA] px-2 py-1 rounded text-left">
                                            <UserAvatar user={u} className="w-5 h-5" textClassName="text-[9px]" />
                                            <span className="truncate">{u.name}</span>
                                            <span className="ml-auto text-[10px] text-[#9CA3AF] truncate">{u.email}</span>
                                        </button>
                                    ));
                                })()}
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Prioritate</label>
                        <select
                            data-testid="dup-task-priority"
                            value={priority}
                            onChange={e => setPriority(e.target.value)}
                            className="w-full px-2 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white"
                        >
                            {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-[#4B5563] uppercase tracking-wide mb-1.5">Termen limită</label>
                        <input
                            data-testid="dup-task-due-date"
                            type="datetime-local"
                            value={dueDate}
                            onChange={e => setDueDate(e.target.value)}
                            className="w-full px-3 py-2 border border-[#E4E4E7] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                        />
                    </div>
                    {hasChecklist && (
                        <label className="flex items-center gap-2 text-sm text-[#4B5563] cursor-pointer select-none">
                            <input
                                data-testid="dup-task-copy-checklist"
                                type="checkbox"
                                checked={copyChecklist}
                                onChange={e => setCopyChecklist(e.target.checked)}
                                className="w-4 h-4 accent-[#002FA7]"
                            />
                            Copiază checklist-ul ({task.checklist.length} puncte, nebifate)
                        </label>
                    )}
                    <div className="flex gap-2 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 py-2 border border-[#E4E4E7] rounded-md text-sm text-[#4B5563] hover:bg-[#F8F9FA] transition-colors">
                            Anulează
                        </button>
                        <button
                            data-testid="duplicate-task-submit-btn"
                            type="submit"
                            disabled={loading}
                            className="flex-1 flex items-center justify-center gap-2 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] transition-colors disabled:opacity-60"
                        >
                            {loading && <Loader2 size={14} className="animate-spin" />}
                            Creează duplicat
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
